import { captureException } from '@sentry/react';
import { queryOptions } from '@tanstack/react-query';
import z from 'zod';

import { authorizedAxiosInstance } from '@/clients';

import { FinancialOverviewInstitution } from '../../types';

const optionalString = z
    .string()
    .nullable()
    .optional()
    .transform((value) => value?.trim() || undefined);
const optionalNumber = z
    .union([z.number(), z.string()])
    .nullable()
    .optional()
    .transform((value) => {
        if (value === null || value === undefined || value === '') {
            return undefined;
        }

        const number = Number(value);
        return Number.isFinite(number) ? number : undefined;
    });

const institutionalHoldingSchema = z
    .object({
        domain: optionalString,
        filingDate: optionalString,
        holder: optionalString,
        investorName: optionalString,
        lastSharesNumber: optionalNumber,
        website: optionalString,
        websiteUrl: optionalString,
    })
    .passthrough();

const individualHoldingSchema = z
    .object({
        filingDate: optionalString,
        link: optionalString,
        reportingName: optionalString,
        securitiesOwned: optionalNumber,
        typeOfOwner: optionalString,
    })
    .passthrough();

const institutionalHoldingsSchema = z.array(institutionalHoldingSchema);
const individualHoldingsSchema = z.array(individualHoldingSchema);

export interface FinancialOverviewIndividualHolding {
    filingDate?: string;
    link?: string;
    reportingName?: string;
    securitiesOwned?: number;
    typeOfOwner?: string;
}

const normalizeTickerSymbol = (tickerSymbol: string) => tickerSymbol.trim().toLocaleUpperCase();

const fetchInstitutionalHoldings = async (tickerSymbol: string, signal: AbortSignal) => {
    try {
        const response = await authorizedAxiosInstance.get('/api/vendors/fmp/institutional/holdings', {
            params: { last: 50, symbol: normalizeTickerSymbol(tickerSymbol) },
            signal,
        });

        return institutionalHoldingsSchema.parse(response.data) as FinancialOverviewInstitution[];
    } catch (error) {
        if (!signal.aborted) {
            captureException(error);
        }
        throw error;
    }
};

const fetchIndividualHoldings = async (tickerSymbol: string, signal: AbortSignal) => {
    try {
        const response = await authorizedAxiosInstance.get('/api/vendors/fmp/insiders/ownership', {
            params: { symbol: normalizeTickerSymbol(tickerSymbol) },
            signal,
        });

        const latestByPerson = new Map<string, FinancialOverviewIndividualHolding>();
        individualHoldingsSchema.parse(response.data).forEach((holding) => {
            if (!holding.reportingName) {
                return;
            }

            const key = holding.reportingName.toLocaleLowerCase();
            const current = latestByPerson.get(key);

            if ((holding.securitiesOwned ?? -1) > (current?.securitiesOwned ?? -1)) {
                latestByPerson.set(key, holding);
            }
        });

        return [...latestByPerson.values()].sort(
            (left, right) => (right.securitiesOwned ?? 0) - (left.securitiesOwned ?? 0),
        );
    } catch (error) {
        if (!signal.aborted) {
            captureException(error);
        }
        throw error;
    }
};

export const financialOverviewHoldingsQueryKeys = {
    individuals: (tickerSymbol: string) =>
        ['financial-overview', 'holdings', normalizeTickerSymbol(tickerSymbol), 'individuals'] as const,
    institutions: (tickerSymbol: string) =>
        ['financial-overview', 'holdings', normalizeTickerSymbol(tickerSymbol), 'institutions'] as const,
};

export const institutionalHoldingsQueryOptions = (tickerSymbol: string) =>
    queryOptions({
        queryFn: ({ signal }) => fetchInstitutionalHoldings(tickerSymbol, signal),
        queryKey: financialOverviewHoldingsQueryKeys.institutions(tickerSymbol),
        staleTime: 15 * 60 * 1_000,
    });

export const individualHoldingsQueryOptions = (tickerSymbol: string) =>
    queryOptions({
        queryFn: ({ signal }) => fetchIndividualHoldings(tickerSymbol, signal),
        queryKey: financialOverviewHoldingsQueryKeys.individuals(tickerSymbol),
        staleTime: 15 * 60 * 1_000,
    });
